import { useEffect, useRef } from "react";
import { useRouterState } from "@tanstack/react-router";

/**
 * Scrolls to the top on forward navigations (push/replace) between pathnames.
 * Back/forward (popstate) is left alone so the browser can restore position.
 * Ignores in-page hash-only changes.
 */
export function ScrollToTopOnNavigate() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const hash = useRouterState({ select: (s) => s.location.hash });
  const isPop = useRef(false);
  const lastPath = useRef<string | null>(null);

  useEffect(() => {
    const onPop = () => {
      isPop.current = true;
      // reset shortly after so the following push isn't misread
      setTimeout(() => (isPop.current = false), 0);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  useEffect(() => {
    if (lastPath.current === pathname) return; // same page, ignore
    lastPath.current = pathname;
    if (isPop.current) return; // let browser restore
    if (hash) return; // anchor link handles its own scroll
    window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
  }, [pathname, hash]);

  return null;
}
